import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { CompletionConditionType, MissionStatus } from '@flipster/apis/v1/rewardHub/types';
import { FormattedMessage } from '@flipster/i18n';
import { useSupportedLocale } from '@flipster/i18n/hooks';
import { Badge } from '@flipster/react/design-system/badge';
import Formatted from '@flipster/react/design-utils/formatted';
import CandlestickIcon from '@flipster/react/icon/generated/CandlestickIcon';
import ChartBoxIcon from '@flipster/react/icon/generated/ChartBoxIcon';
import CheckCircleIcon from '@flipster/react/icon/generated/CheckCircleIcon';
import CircleStackIcon from '@flipster/react/icon/generated/CircleStackIcon';
import ClockIcon from '@flipster/react/icon/generated/ClockIcon';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import { useAuth } from '../../../../../shared/auth/state';
import { rewardHubV2Bunja } from '../../../state';
import { RegularMission } from '../../../types';
import { DistributionDate, MissionTitle, ProgressBarItem } from '../../misc';
import { timelineDot, timelineDotWrapper, timelineLeft } from '../../styles';
import {
  getDaysUntilExpiration,
  getIsDepositAmountCondition,
  getIsTradingVolumeCondition,
  getLocalizedText,
  getMissionStatusFlags,
  getRewardAmount,
  getRewardCurrency,
} from '../../utils';

interface Props {
  mission: RegularMission;
  completionConditions: CompletionConditionType[];
  timeline?: boolean;
  isLast?: boolean;
  isFirst?: boolean;
}

export const MobileMilestoneItem = ({ mission, completionConditions, timeline, isLast, isFirst }: Props) => {
  const locale = useSupportedLocale();
  const { user } = useAuth();
  const { feeVoucherRewardAtom } = useBunja(rewardHubV2Bunja);
  const feeVoucherRewardRecord = useAtomValue(feeVoucherRewardAtom);

  const spec = mission?.spec;
  const status = mission?.status;
  if (!spec) return null;

  const completionRewards = spec.completionRewards;
  const completionReward = completionRewards[0];

  const { isExpired, isClaimed, isRejected } = getMissionStatusFlags(status);
  const isActive = mission?.isActive;
  const progress = status?.progress;

  const missionTitle = getLocalizedText(mission.skin?.body?.localizationTable, locale);

  const daysUntilExpiration = getDaysUntilExpiration(spec.validityConditions, user?.createdAt);

  return (
    <div className={flex({ gap: 16, py: 16 })}>
      <div className={timelineLeft({ timeline, position: isFirst ? 'first' : isLast ? 'last' : 'middle' })}>
        <div className={timelineDotWrapper({ isFirst, isLast })}>
          {progress && (
            <MilestoneIcon completionConditions={completionConditions} status={status} isActive={isActive} />
          )}
          {!progress && <MilestoneIcon completionConditions={completionConditions} status={status} isActive={false} />}
        </div>
      </div>
      <div className={css({ flex: 1 })}>
        {daysUntilExpiration && (
          <Badge type='tint' color='promotion' leadingIcon={<ClockIcon size={16} />}>
            <FormattedMessage
              id='Reward_hub.r250213.Reward_hub_0213_days_left'
              values={{ value: daysUntilExpiration }}
            />
          </Badge>
        )}
        <MissionTitle
          missionIndex={mission.missionIndex}
          sectionIndex={mission.sectionIndex}
          title={
            <div>
              <Formatted.Number
                value={getRewardAmount(completionReward, feeVoucherRewardRecord)}
                precision={0}
                toFixed
                css={{
                  textStyle: 'Text/S/Strong',
                  color: 'content.accent.promotion',
                }}
                suffix={getRewardCurrency(completionReward)}
              />
              <div className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5' })}>
                {missionTitle}
              </div>
            </div>
          }
          completionReward={completionReward}
          status={status}
          isActive={isActive}
        />
        <ProgressBarItem status={status} isHide={isExpired || isClaimed} />
        <DistributionDate
          date={status?.settleAt}
          showMaintainMessage={spec.completionConditions.some(getIsDepositAmountCondition)}
          isHide={isExpired || isRejected}
        />
      </div>
    </div>
  );
};

const iconSize = 16;

export const MilestoneIcon = ({
  completionConditions,
  status,
  isActive,
}: {
  completionConditions: CompletionConditionType[];
  status: MissionStatus;
  isActive?: boolean;
}) => {
  const hasMultiProcess = completionConditions?.length && completionConditions.length > 1;

  const isDepositAmountCondition = completionConditions?.some(getIsDepositAmountCondition);
  const isTradingVolumeCondition = completionConditions?.some(getIsTradingVolumeCondition);

  const { isAssigned, isClaimed, isComplete, isSettled, isRejected } = getMissionStatusFlags(status);

  if ((isComplete && !isRejected) || isClaimed || isSettled)
    return (
      <CheckCircleIcon
        size={28}
        color={
          isClaimed || isSettled ? token('colors.background.default.level-4') : token('colors.content.default.level-5')
        }
      />
    );

  const iconColor = isActive ? token('colors.content.default.level-5') : token('colors.content.default.level-2');

  const Icon = () => {
    if (hasMultiProcess) return <ChartBoxIcon size={iconSize} color={iconColor} />;
    if (isAssigned) return <CircleStackIcon size={iconSize} color={iconColor} />;
    if (isDepositAmountCondition) return <CircleStackIcon size={iconSize} color={iconColor} />;
    if (isTradingVolumeCondition) return <CandlestickIcon size={iconSize} color={iconColor} />;
  };
  return (
    <div className={timelineDot({ state: isActive ? 'current' : undefined })}>
      <Icon />
    </div>
  );
};
